/** @format */
import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import { FadeLoader } from "react-spinners";
import { ReactComponent as BagIcon } from "images/svg/bag.svg";
import { ReactComponent as SearchIcon } from "images/svg/search.svg";
import { ReactComponent as FilterIcon } from "images/svg/filter.svg";
import { ReactComponent as GridViewIcon } from "images/svg/grid.svg";
import { ReactComponent as ChartViewIcon } from "images/svg/chart.svg";
import { ReactComponent as CalendarIcon } from "images/svg/calendar-l.svg";
import { COLUMNS } from "./helpers/peerColumn";
import LeaderboardFilterModal from "../LeaderBoardFilterModal";
import { extractFilterValues } from "utils/functions";
import FeatureUnavailableModal from "../FeatureUnavailable";

const PeerToPeerTable = ({
  P2Pdata,
  metadata,
  auth_user_group,
  isLoading,
  setFilterString,
  setActiveRecord
}) => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const pageSize = metadata?.page_size || 5;

  const [isDropdownVisible] = useState(false);
  const gridIconRef = useRef(null);

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const filterIconRef = useRef(null);
  const [isUnavailableModalVisible, setIsUnavailableModalVisible] = useState(false);

  useEffect(() => {
    if (metadata && metadata.page) {
      setCurrentPage(metadata.page);
    }
  }, [metadata]);

  useEffect(() => {
    if (filter) {
      const values = extractFilterValues(filter);
      const filterString = `page=${currentPage}&page_size=${pageSize}${
        values?.category ? `&category=${values?.category}` : ""
      }${
        values?.gender ? `&gender=${values?.gender}` : ""
      }${
        values?.state?.length > 0
          ? `&state=${values?.state?.join(",")}`
          : ""
      }${
        values?.lga?.length > 0
          ? `&lga=${values?.lga?.join(",")}`
          : ""
      }${
        values?.course?.length > 0
          ? `&course=${values?.course?.join(",")}`
          : ""
      }${
        values?.min_score ? `&min_score=${values?.min_score}` : ""
      }${
        values?.max_score ? `&max_score=${values?.max_score}` : ""
      }`;
      setFilterString(filterString);
    }
  }, [filter, currentPage, pageSize, setFilterString]);

  const toggleUnavailableModal = () => { 
    setIsFilterModalVisible(false);
    setIsUnavailableModalVisible(!isUnavailableModalVisible);
  };

  const toggleFilterModal = () => {
    setIsFilterModalVisible(!isFilterModalVisible);
  };

  const getFilterModalPosition = () => {
    if (filterIconRef.current) {
      const rect = filterIconRef.current.getBoundingClientRect();
      return {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      };
    }
    return { top: 0, left: 0 };
  };
  
  useEffect(() => {
    setData(P2Pdata);
  }, [isLoading, P2Pdata]);

  const columns = useMemo(() => COLUMNS(setActiveRecord), [setActiveRecord]);

  // Calculate total pages based on metadata
  const totalPages = metadata ? Math.ceil(metadata.count / pageSize) : 0;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize },
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      
      // Build the filter string with the new page
      if (filter) {
        const values = extractFilterValues(filter);
        const filterString = `page=${newPage}&page_size=${pageSize}${
          values?.category ? `&category=${values?.category}` : ""
        }${
          values?.gender ? `&gender=${values?.gender}` : ""
        }${
          values?.state?.length > 0
            ? `&state=${values?.state?.join(",")}`
            : ""
        }${
          values?.lga?.length > 0
            ? `&lga=${values?.lga?.join(",")}`
            : ""
        }${
          values?.course?.length > 0
            ? `&course=${values?.course?.join(",")}`
            : ""
        }${
          values?.min_score ? `&min_score=${values?.min_score}` : ""
        }${
          values?.max_score ? `&max_score=${values?.max_score}` : ""
        }`;
        setFilterString(filterString);
      } else if (searchTerm) {
        setFilterString(`page=${newPage}&page_size=${pageSize}&search=${searchTerm}`);
      } else {
        setFilterString(`page=${newPage}&page_size=${pageSize}`);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFilterString(`page=1&page_size=${pageSize}&search=${searchTerm}`);
  };

  const authUserGroupId = auth_user_group?.id;

  return (
    <div>
      <div className="leaderboard-card">
        <div className="leaderboard-card-body">
          <div className="leaderboard-header">
            <div className="filter-controls">
              <button 
                className={`filter-btn ${isDropdownVisible ? 'active' : ''}`} 
                onClick={toggleUnavailableModal}
                ref={gridIconRef}
              >
                <GridViewIcon />
              </button>
              <button 
                className="filter-btn" 
                onClick={toggleUnavailableModal}
              >
                <ChartViewIcon />
              </button>
            </div>
            
            <div className="search-wrapper">
              <div className="search-icon">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="search-input"
                placeholder="Search by group name or course"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>
            
            <div className="filter-controls">
              <button 
                className={`filter-btn ${isFilterModalVisible ? 'active' : ''}`} 
                onClick={toggleFilterModal} 
                ref={filterIconRef}
              >
                <FilterIcon />
              </button>
              <button className="filter-btn" onClick={toggleUnavailableModal}>
                <CalendarIcon />
              </button>
            </div>
          </div>
          
          <LeaderboardFilterModal 
            isVisible={isFilterModalVisible} 
            position={getFilterModalPosition()}
            setIsFilterModalVisible={setIsFilterModalVisible}
            setFilter={setFilter}
            setFilterString={setFilterString}
            setActiveRecord={setActiveRecord}
          />
          <FeatureUnavailableModal 
            isVisible={isUnavailableModalVisible} 
            setIsVisible={setIsUnavailableModalVisible}
          />

          {isLoading ? (
            <div className="loading-container">
              <FadeLoader color="#36d7b7" />
            </div>
          ) : (
            <div>
              {data?.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table
                      {...getTableProps()}
                      className="leaderboard-table"
                    >
                      <thead>
                        {headerGroups.map((headerGroup, idx) => (
                          <tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                            {headerGroup.headers.map((column, colIdx) => (
                              <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                className="leaderboard-header-cell"
                                key={colIdx}
                              >
                                {column.render("Header")}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.map((row, idx) => {
                          prepareRow(row);
                          const isCurrentUser = row.original.id === authUserGroupId;
                          return (
                            <tr 
                              {...row.getRowProps()}
                              className={`leaderboard-row ${isCurrentUser ? 'current-user-row' : ''}`}
                              key={idx}
                            >
                              {row.cells.map((cell, cellIdx) => {
                                return (
                                  <td 
                                    {...cell.getCellProps()}
                                    className="leaderboard-cell"
                                    key={cellIdx}
                                  >
                                    {cell.render("Cell")}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <div className="pagination-container">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        &laquo;
                      </button>
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        &lt;
                      </button>
                      
                      {[...Array(Math.min(totalPages, 10))].map((_, i) => (
                        <button
                          key={i}
                          className={`pagination-btn ${currentPage === i + 1 ? 'pagination-btn-active' : ''}`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      
                      {totalPages > 10 && (
                        <span className="pagination-ellipsis">...</span>
                      )}
                      
                      {totalPages > 10 && (
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                        </button>
                      )}
                      
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        &gt;
                      </button>
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <BagIcon />
                  <p className="empty-state-message">
                    No peer to peer data available yet.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeerToPeerTable;